import { useMutation } from '@apollo/client/react';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { Grid } from '@mui/material';
import { EssealDataTable } from 'esseal-data-table';
import React, { useContext } from 'react';

import DiarySingleEntry from './DiarySingleEntry';
import diaryEntryColumns from './tableColumns';

import PermissionContext from '@/Contexts/Permissions';
import { ReportContext } from '@/Contexts/ReportContext';
import {
  type Issue,
  PermissionActionEnum,
  PermissionEntityEnum,
  RemoveIssueDocument,
  GetReportIssuesDocument,
  UpdateReportIssueDocument,
} from '@/graphql/generated/graphql';

interface DiaryEntryTableProps {
  data: Issue[];
  selectedDiaryEntry: Issue | null;
  setSelectedDiaryEntry: React.Dispatch<React.SetStateAction<Issue | null>>;
}

const DiaryEntryTable: React.FC<DiaryEntryTableProps> = ({ data, selectedDiaryEntry, setSelectedDiaryEntry }) => {
  const { selectedReport } = useContext(ReportContext);
  const { canPerformAction } = useContext(PermissionContext);
  const { Manage } = PermissionActionEnum;
  const { Reports } = PermissionEntityEnum;

  const [removeIssue] = useMutation(RemoveIssueDocument, {
    refetchQueries: [
      {
        query: GetReportIssuesDocument,
        variables: {
          reportId: selectedReport?.id as string,
        },
      },
    ],
  });

  const [updateIssue] = useMutation(UpdateReportIssueDocument, {
    refetchQueries: [
      {
        query: GetReportIssuesDocument,
        variables: {
          reportId: selectedReport?.id as string,
        },
      },
    ],
  });

  const handleUpdate = (
    value: string,
    field: 'startTime' | 'endTime' | 'issueType' | 'description',
    id: string,
    description?: { text: string; categoryIds: string[] },
  ) => {
    if (field === 'startTime' || field === 'endTime') {
      const currentDate = new Date();
      const [hours, minutes] = value.split(':').map(Number);
      const updatedDate = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        currentDate.getDate(),
        hours,
        minutes,
      );
      value = updatedDate.toISOString();
    }

    if (field === 'description' && description) {
      updateIssue({
        variables: {
          reportIssue: {
            id: id,
            description: description.text,
            categoryIds: description.categoryIds,
          },
        },
      });
    } else {
      updateIssue({
        variables: {
          reportIssue: {
            id: id,
            [field]: value,
          },
        },
      });
    }
  };

  const removeEntry = (id: string) => {
    if (!canPerformAction(Manage, Reports)) return;

    removeIssue({ variables: { issueId: id } });
  };

  return (
    <Grid container size={12} spacing={2} data-testid="diary-entry-table" height="calc(100vh - 610px)">
      <Grid size={selectedDiaryEntry ? 8 : 12}>
        <EssealDataTable
          rows={data}
          columns={diaryEntryColumns(handleUpdate)}
          pagination={true}
          pageSize={25}
          // Actions & Events
          rowActions={(row) => [
            {
              label: 'Edit',
              icon: <EditIcon fontSize="small" color="primary" />,
              tooltip: 'Edit Entry',
              onClick: () => setSelectedDiaryEntry(row),
            },
            {
              label: 'Delete',
              icon: <DeleteIcon fontSize="small" color="primary" />,
              tooltip: 'Delete Entry',
              onClick: () => removeEntry(row.id),
            },
          ]}
        />
      </Grid>
      {selectedDiaryEntry && (
        <Grid size={4}>
          {selectedDiaryEntry && (
            <DiarySingleEntry
              key={selectedDiaryEntry.id}
              selectedDiaryEntry={selectedDiaryEntry}
              setSelectedDiaryEntry={setSelectedDiaryEntry}
            />
          )}
        </Grid>
      )}
    </Grid>
  );
};

export default DiaryEntryTable;
